import { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import type { SyncLogEntry } from '@/api/types';

/**
 * Tiny inline SVG sparkline: error count per UTC day over the past 14 days.
 * No charting library — the data is at most a few dozen points.
 */
export function ErrorsSparkline({
  log,
  days = 14,
  height = 56,
}: {
  log: SyncLogEntry[];
  days?: number;
  height?: number;
}) {
  const theme = useTheme();

  const series = useMemo(() => {
    const buckets: number[] = new Array(days).fill(0);
    const now = Date.now();
    const dayMs = 24 * 60 * 60_000;
    for (const entry of log) {
      const ageMs = now - new Date(entry.run_at).getTime();
      const idx = days - 1 - Math.floor(ageMs / dayMs);
      if (idx >= 0 && idx < days) buckets[idx] += entry.errors;
    }
    return buckets;
  }, [log, days]);

  const max = Math.max(1, ...series);
  const W = 600;
  const stepX = W / Math.max(1, series.length - 1);
  const points = series.map((v, i) => `${(i * stepX).toFixed(2)},${(height - (v / max) * (height - 8) - 4).toFixed(2)}`);
  const polyline = points.join(' ');
  const totalErrors = series.reduce((a, b) => a + b, 0);

  return (
    <Box sx={{ width: '100%' }}>
      <svg
        role="img"
        aria-label={`Errors per day, last ${days} days (total ${totalErrors})`}
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
      >
        <line
          x1="0"
          x2={W}
          y1={height - 4}
          y2={height - 4}
          stroke={theme.palette.divider}
          strokeWidth={1}
        />
        <polyline
          fill="none"
          stroke={totalErrors > 0 ? theme.palette.error.main : theme.palette.primary.main}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          points={polyline}
        />
        {series.map((v, i) =>
          v === 0 ? null : (
            <circle
              key={i}
              cx={i * stepX}
              cy={height - (v / max) * (height - 8) - 4}
              r={2}
              fill={theme.palette.error.main}
            />
          ),
        )}
      </svg>
    </Box>
  );
}
